import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ArrowLeft, Receipt, Check, ChevronRight, Lock, Trash2, ToggleLeft, ToggleRight, Mail, MessageCircle } from 'lucide-react';
import api from '@/api';

const COMPANY_LOGO = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/o57rf0lw_Global%20Business%20Connects%20logo.png';
const COMPANY_DETAILS = {
  name: 'Global Business Links Ltd',
  address: '3 Lower Village, Haywards Heath',
  country: 'UK',
  postcode: 'RH16 4GR',
  vatNumber: '483692352',
};

export const openReceiptInNewTab = async (sessionId) => {
  try {
    const receipt = await api.getReceipt(sessionId);
    if (!receipt) return;
    const date = receipt.paid_at
      ? new Date(receipt.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : 'N/A';
    const invoiceNum = `INV-${sessionId.slice(-8).toUpperCase()}`;
    const curr = receipt.currency?.toUpperCase() === 'GBP' ? '\u00A3'
      : receipt.currency?.toUpperCase() === 'EUR' ? '\u20AC'
      : receipt.currency?.toUpperCase() === 'JPY' ? '\u00A5'
      : receipt.currency?.toUpperCase() === 'INR' ? '\u20B9'
      : receipt.currency?.toUpperCase() === 'CAD' ? 'CA$'
      : receipt.currency?.toUpperCase() === 'AUD' ? 'A$'
      : '$';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>VAT Receipt - ${invoiceNum}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:40px;max-width:800px;margin:0 auto}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px}.logo{height:48px}
.title{text-align:right}.title h1{font-size:28px;font-weight:800;color:#111}.title p{font-size:13px;color:#888;margin-top:4px}
.details{display:flex;justify-content:space-between;margin-bottom:32px}.details-block h3{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#999;margin-bottom:8px}
.details-block p{font-size:13px;color:#444;line-height:1.6}
.table{width:100%;border-collapse:collapse;margin-bottom:24px}.table th{text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#999;padding:12px 0;border-bottom:1px solid #eee}
.table th:last-child{text-align:right}.table td{padding:16px 0;font-size:14px;border-bottom:1px solid #f5f5f5}.table td:last-child{text-align:right;font-weight:600}
.totals{margin-left:auto;width:280px}.totals-row{display:flex;justify-content:space-between;padding:8px 0;font-size:13px;color:#666}
.totals-row.total{border-top:2px solid #111;padding-top:12px;margin-top:4px;font-size:16px;font-weight:800;color:#111}
.footer{margin-top:48px;padding-top:24px;border-top:1px solid #eee;font-size:11px;color:#aaa;text-align:center;line-height:1.8}
.actions{text-align:center;margin-top:24px;display:flex;gap:12px;justify-content:center}
.btn{padding:10px 24px;border-radius:12px;border:none;font-size:14px;font-weight:600;cursor:pointer}
.btn-back{background:#f5f5f5;border:1px solid #e5e5e5;color:#444}.btn-back:hover{background:#eee}
.btn-print{background:#111;color:#fff}.btn-print:hover{background:#333}
.no-print{margin-bottom:20px}@media print{.no-print{display:none!important}}</style></head><body>
<div class="no-print"><button class="btn btn-back" onclick="window.close()">&larr; Close</button></div>
<div class="header"><img src="${COMPANY_LOGO}" class="logo" alt="${COMPANY_DETAILS.name}" /><div class="title"><h1>VAT Receipt</h1><p>${invoiceNum} &middot; ${date}</p></div></div>
<div class="details"><div class="details-block"><h3>From</h3><p><strong>${COMPANY_DETAILS.name}</strong></p><p>${COMPANY_DETAILS.address}</p><p>${COMPANY_DETAILS.country} ${COMPANY_DETAILS.postcode}</p><p>VAT: ${COMPANY_DETAILS.vatNumber}</p></div>
<div class="details-block" style="text-align:right"><h3>To</h3><p><strong>${receipt.customer_name || 'Customer'}</strong></p><p>${receipt.customer_email || ''}</p></div></div>
<table class="table"><thead><tr><th>Description</th><th>Amount</th></tr></thead><tbody><tr><td>WTFHappened Premium - Monthly Subscription</td><td>${curr}${receipt.base_amount?.toFixed(2)}</td></tr></tbody></table>
<div class="totals"><div class="totals-row"><span>Subtotal</span><span>${curr}${receipt.base_amount?.toFixed(2)}</span></div><div class="totals-row"><span>VAT (${receipt.vat_rate || 20}%)</span><span>${curr}${receipt.vat_amount?.toFixed(2)}</span></div><div class="totals-row total"><span>Total</span><span>${curr}${receipt.total_amount?.toFixed(2)}</span></div></div>
<div class="footer"><p>${COMPANY_DETAILS.name} &middot; VAT Reg: ${COMPANY_DETAILS.vatNumber}</p><p>Thank you for your payment.</p></div>
<div class="actions no-print"><button class="btn btn-print" onclick="window.print()">Print / Save as PDF</button></div>
</body></html>`);
    printWindow.document.close();
  } catch (e) {
    // Receipt generation failed silently
  }
};

export function SubscriptionTab({ subInfo, user, showPaymentHistory, setShowPaymentHistory, payments, loadingPayments, onOpenPaymentHistory, onSubscribe, onToggleAutoRenew, onCancelClick, pricing }) {
  const symbol = pricing?.symbol || '£';
  const price = pricing?.price ?? 5.99;
  const priceDisplay = `${symbol}${Number.isInteger(price) ? price : price.toFixed(2)}`;

  if (showPaymentHistory) {
    return (
      <motion.div key="payment-history" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <button data-testid="payment-history-back" onClick={() => setShowPaymentHistory(false)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
          <ArrowLeft size={16} /> Back to Subscription
        </button>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase mb-4">Payment History</p>
          {loadingPayments ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
          ) : payments.length === 0 ? (
            <div data-testid="no-payments" className="text-center py-8">
              <Receipt size={32} className="mx-auto text-gray-200 mb-2" />
              <p className="text-sm text-gray-400">No payments yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map((txn, i) => {
                const curr = txn.currency?.toUpperCase() === 'GBP' ? '\u00A3'
                  : txn.currency?.toUpperCase() === 'EUR' ? '\u20AC'
                  : txn.currency?.toUpperCase() === 'JPY' ? '\u00A5'
                  : txn.currency?.toUpperCase() === 'INR' ? '\u20B9'
                  : txn.currency?.toUpperCase() === 'CAD' ? 'CA$'
                  : txn.currency?.toUpperCase() === 'AUD' ? 'A$'
                  : '$';
                const displayAmount = txn.total_amount || txn.amount || 0;
                return (
                  <button key={txn.id || i} data-testid={`payment-item-${i}`}
                    onClick={() => txn.session_id && openReceiptInNewTab(txn.session_id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:border-gray-200 transition-all text-left cursor-pointer active:scale-[0.98]">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-green-50">
                      <Check size={14} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {curr}{displayAmount.toFixed(2)}
                        <span className="ml-1 text-[10px] font-normal text-gray-400">({curr}{txn.base_amount?.toFixed(2)} + VAT)</span>
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {txn.paid_at ? new Date(txn.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'Date unavailable'}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-gray-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div key="sub" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4">
        <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase mb-3">Current Plan</p>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-bold text-gray-900 capitalize">
              {subInfo?.subscription_status === 'active' ? 'Premium' : 'Free'}
            </p>
            {subInfo?.subscription_status !== 'active' && <p className="text-xs text-gray-400">Upgrade to unlock premium features</p>}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${subInfo?.subscription_status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
            {subInfo?.subscription_status === 'active' ? 'Active' : 'Free'}
          </span>
        </div>
        {subInfo?.subscription_status !== 'active' && (
          <button data-testid="subscribe-btn" onClick={onSubscribe}
            className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-all">
            Subscribe - {priceDisplay}/month + VAT
          </button>
        )}
      </div>

      {subInfo?.subscription_status === 'active' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Auto-renew</p>
              <p className="text-xs text-gray-400">Automatically renew your subscription</p>
            </div>
            <button data-testid="auto-renew-toggle" onClick={onToggleAutoRenew} className="text-gray-900">
              {subInfo?.auto_renew ? <ToggleRight size={32} className="text-green-500" /> : <ToggleLeft size={32} className="text-gray-300" />}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {subInfo?.subscription_status === 'active' && (
          <button data-testid="cancel-subscription-btn" onClick={onCancelClick}
            className="w-full bg-white rounded-2xl border border-red-100 p-4 flex items-center gap-3 text-left hover:bg-red-50 transition-colors">
            <Trash2 size={18} className="text-red-400" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-600">Cancel Subscription</p>
              <p className="text-xs text-gray-400">Cancel your subscription</p>
            </div>
            <ChevronRight size={16} className="text-red-300" />
          </button>
        )}
        <button data-testid="payment-history-btn" onClick={onOpenPaymentHistory}
          className="w-full bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
          <Receipt size={18} className="text-gray-500" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Payment History</p>
            <p className="text-xs text-gray-400">View receipts and past transactions</p>
          </div>
          <ChevronRight size={16} className="text-gray-300" />
        </button>
        <a data-testid="support-btn" href="mailto:support@wtfhappened.world"
          className="w-full bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
          <Mail size={18} className="text-gray-500" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Support</p>
            <p className="text-xs text-gray-400">Get help via email</p>
          </div>
          <ChevronRight size={16} className="text-gray-300" />
        </a>
        {user?.subscription_status === 'active' ? (
          <a data-testid="whatsapp-digest-btn" href="https://wa.me/15559021743?text=Hi%20WTFHappened%20%F0%9F%91%8B%20Send%20me%20today%27s%20digest!" target="_blank" rel="noopener noreferrer"
            className="w-full bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
            <MessageCircle size={18} className="text-green-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Daily Digest on WhatsApp</p>
              <p className="text-xs text-gray-400">Get today's news sent to your WhatsApp</p>
            </div>
            <ChevronRight size={16} className="text-gray-300" />
          </a>
        ) : (
          <button data-testid="whatsapp-digest-locked" onClick={onSubscribe}
            className="w-full bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
            <MessageCircle size={18} className="text-green-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Daily Digest on WhatsApp</p>
              <p className="text-xs text-gray-400">Subscribe to unlock WhatsApp digests + premium features</p>
            </div>
            <Lock size={14} className="text-gray-400" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
